import React from 'react';
import * as Sentry from '@sentry/react';
import { trackEvent } from '../lib/analytics';

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    try {
      trackEvent('frontend_error', {
        scope: this.props.scope || 'app',
        error: error?.message,
        stack: error?.stack,
        componentStack: errorInfo?.componentStack,
      });
    } catch (e) {
      console.error('ErrorBoundary analytics failed', e);
    }
    // #813: ship to Sentry in parallel. No-op when Sentry isn't initialized
    // (no DSN), so the guard is implicit.
    try {
      Sentry.withScope((scope) => {
        scope.setTag('error_boundary_scope', this.props.scope || 'unknown');
        scope.setExtra('componentStack', errorInfo?.componentStack);
        Sentry.captureException(error);
      });
    } catch (e) {
      console.error('ErrorBoundary Sentry capture failed', e);
    }
    console.error('ErrorBoundary caught error', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        role="alert"
        className="min-h-screen w-full flex items-center justify-center bg-[#0A0A0A] text-[#F5F1E8] px-6"
      >
        <div className="max-w-xl w-full border border-[#1E1E1E] bg-[#1E1E1E] p-8 rounded">
          <div className="flex items-center gap-4 mb-6">
            <svg
              aria-hidden="true"
              width="40"
              height="40"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#FF4D00"
              strokeWidth="2"
              strokeLinecap="square"
              strokeLinejoin="miter"
            >
              <path d="M12 3 L22 21 L2 21 Z" />
              <line x1="12" y1="10" x2="12" y2="15" />
              <line x1="12" y1="18" x2="12" y2="18" />
            </svg>
            <h1
              className="text-2xl uppercase tracking-wider font-bold"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Something Broke
            </h1>
          </div>
          <p className="text-[#F5F1E8] mb-2">
            An unexpected error stopped this view from rendering. Your session is safe.
          </p>
          <p className="text-sm text-[#A0A0A0] mb-6" style={{ fontFamily: "'IBM Plex Mono', monospace" }}>
            {this.state.error?.message || 'Unknown render error'}
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={this.handleReload}
              className="px-4 py-2 bg-[#FF4D00] text-[#0A0A0A] uppercase tracking-wider font-bold hover:bg-[#E04400] transition-colors"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Reload
            </button>
            <a
              href="mailto:support@getfray.ai?subject=Fray%20render%20error"
              className="px-4 py-2 border border-[#F5F1E8] text-[#F5F1E8] uppercase tracking-wider font-bold hover:bg-[#F5F1E8] hover:text-[#0A0A0A] transition-colors"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}
            >
              Contact Support
            </a>
          </div>
        </div>
      </div>
    );
  }
}
